import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldCheck, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';

// Second step of a master-admin sign-in.
//   mode "verify": enter the 6-digit code (or a one-time recovery code).
//   mode "setup":  first time. Add the account to an authenticator app, confirm a code, save recovery codes.
export default function TwoFactorStep({ mode, mfaToken, onDone, onCancel }) {
  const { twoFactorSetup, twoFactorEnable, twoFactorVerify } = useAuth();
  const [setup, setSetup] = useState(null);       // { secret, otpauth_uri }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null); // shown ONCE after enabling
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (mode !== 'setup') return;
    twoFactorSetup(mfaToken).then(setSetup).catch((e) => setError(e.message));
  }, [mode, mfaToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      if (mode === 'setup') {
        const r = await twoFactorEnable(mfaToken, code);
        setRecovery(r.recovery_codes);
      } else {
        await twoFactorVerify(mfaToken, code);
        toast.success('Signed in successfully');
        onDone();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success('Copied'); };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-4" style={{ backgroundColor: '#000e2a' }}>
      <div className="relative z-10 w-full max-w-md animate-slide-up">
        <div className="text-center mb-5">
          <img src="/hat-logo.png" alt="BIG Hat" className="w-20 h-20 mx-auto mb-3 object-contain" />
          <h1 className="text-3xl font-bold font-lemonada" style={{ color: '#fbdd68' }}>BIG Hat</h1>
        </div>
        <Card className="glass-card rounded-2xl border-0 bg-transparent">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-[#fbdd68]">
              <ShieldCheck className="w-5 h-5" />
              <h2 className="text-lg font-bold">{mode === 'setup' ? 'Set up two-factor sign-in' : 'Two-factor sign-in'}</h2>
            </div>

            {error && (
              <div data-testid="mfa-error" className="bg-[#ef4444]/10 border border-[#ef4444]/30 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#ef4444]" />
                <p className="text-sm text-[#ef4444]">{error}</p>
              </div>
            )}

            {recovery ? (
              <div className="space-y-3" data-testid="mfa-recovery">
                <p className="text-sm text-white">Two-factor is on. Save these recovery codes somewhere safe. Each works once if you lose your phone. <b>They are shown only now.</b></p>
                <div className="grid grid-cols-2 gap-2 font-mono text-sm text-[#fbdd68] bg-[#000e2a] rounded-lg p-3 border border-[#fbdd68]/15">
                  {recovery.map((c) => <span key={c}>{c}</span>)}
                </div>
                <Button type="button" variant="outline" className="w-full border-[#fbdd68]/30 text-[#fbdd68]" onClick={() => copy(recovery.join('\n'))}>
                  <Copy className="w-4 h-4 mr-2" /> Copy all codes
                </Button>
                <label className="flex items-center gap-2 text-sm text-[#8892b0]">
                  <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-saved-checkbox" />
                  I have saved these codes
                </label>
                <Button className="w-full bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#fbdd68]/90" disabled={!saved} onClick={onDone} data-testid="mfa-continue">
                  Continue
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {mode === 'setup' && (
                  <div className="space-y-2 text-sm text-[#8892b0]">
                    <p>Master admin accounts must use an authenticator app (Google Authenticator, Authy, 1Password). Add this account using the key below, then enter the 6-digit code it shows.</p>
                    {setup ? (
                      <>
                        <div className="bg-[#000e2a] rounded-lg p-3 border border-[#fbdd68]/15">
                          <p className="text-xs mb-1">Setup key</p>
                          <p className="font-mono text-[#fbdd68] break-all" data-testid="mfa-secret">{setup.secret}</p>
                        </div>
                        <a className="text-[#fbdd68] underline text-xs break-all" href={setup.otpauth_uri}>Open in authenticator app</a>
                      </>
                    ) : !error && <p>Preparing...</p>}
                  </div>
                )}
                <Input
                  data-testid="mfa-code-input" inputMode="numeric" autoComplete="one-time-code" autoFocus
                  value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder={mode === 'verify' ? '6-digit code or recovery code' : '6-digit code'}
                  className="bg-[#000e2a] border-[#fbdd68]/20 text-white tracking-widest"
                />
                <Button type="submit" disabled={busy || !code || (mode === 'setup' && !setup)} data-testid="mfa-submit"
                        className="w-full bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#fbdd68]/90">
                  {busy ? 'Checking...' : mode === 'setup' ? 'Turn on two-factor' : 'Verify'}
                </Button>
                <button type="button" onClick={onCancel} className="w-full text-xs text-[#8892b0] hover:text-white">Back to sign in</button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
